import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { VerificationResultPanel } from '@/components/verification/VerificationResultPanel';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import {
  NIN_SLIP_LABELS,
  SLIP_TYPE_FROM_ROUTE,
} from '@/constants/ninSlipFeatures';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { useUserAuth } from '@/context/UserAuthContext';
import { verificationService, getApiErrorMessage } from '@/services';
import type { VerificationPricingRow, VerificationResult } from '@/types/verification';
import {
  buildNinVerificationRequest,
  EMPTY_NIN_DEMOGRAPHY,
  formatDateForNinslip,
  isNinVerifyInputReady,
  NIN_METHOD_LABELS,
  ninInputFieldCopy,
  parseDemographyFromState,
  parseNinVerificationMethodFromState,
  validateNinDemographyFields,
  validateNinVerificationValue,
  type NINDemographyInput,
} from '@/utils/verification/ninVerificationInput';
import { generateRegularNINSlipPDF } from '@/utils/verification/regularNinSlipPdfGenerator';
import { generateStandardNINSlipPDF } from '@/utils/verification/standardNinSlipPdfGenerator';
import { generatePremiumNINSlipPDF } from '@/utils/verification/premiumNinSlipPdfGenerator';
import { generateAdvancedNINSlipPDF } from '@/utils/verification/advancedNinSlipPdfGenerator';
import { notify } from '@/utils/notify';

function formatNaira(amount: number) {
  return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function NinSlipVerifyPage() {
  const { slipType: slipRoute = 'regular' } = useParams<{ slipType: string }>();
  const slipType = SLIP_TYPE_FROM_ROUTE[slipRoute];
  const navigate = useNavigate();
  const location = useLocation();
  const { user, persistUser } = useUserAuth();
  const { loadWallet } = useDashboardUi();

  const verificationMethod = parseNinVerificationMethodFromState(location.state);
  const [pricing, setPricing] = useState<VerificationPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [ninValue, setNinValue] = useState('');
  const [demography, setDemography] = useState<NINDemographyInput>(
    parseDemographyFromState(location.state) || EMPTY_NIN_DEMOGRAPHY,
  );
  const [demographyErrors, setDemographyErrors] = useState<Record<string, string>>({});
  const [showPinModal, setShowPinModal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<VerificationResult | null>(null);

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await verificationService.getPricing();
      setPricing(res.data?.pricing.nin ?? []);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load pricing'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!slipType) {
      navigate(ROUTES.dashboardVerifyNin, { replace: true });
      return;
    }
    loadPricing();
  }, [slipType, navigate, loadPricing]);

  const price = useMemo(
    () =>
      pricing.find((p) => p.slipType === slipType && p.verificationMethod === verificationMethod)
        ?.price,
    [pricing, slipType, verificationMethod],
  );

  const inputCopy = ninInputFieldCopy(verificationMethod);
  const ready = isNinVerifyInputReady(verificationMethod, ninValue, demography);

  async function handleVerify(pin: string) {
    if (!slipType) return;

    if (verificationMethod === 'by_demography') {
      const errors = validateNinDemographyFields(demography);
      setDemographyErrors(errors);
      if (Object.keys(errors).length > 0) {
        notify.error('Please fix the highlighted fields');
        return;
      }
    } else {
      const err = validateNinVerificationValue(verificationMethod, ninValue);
      if (err) {
        notify.error(err);
        return;
      }
    }

    setVerifying(true);
    try {
      const request = buildNinVerificationRequest(verificationMethod, slipType, ninValue, demography);
      const res = await verificationService.verifyNin({ ...request, pin });
      const payload = res.data;

      if (!payload?.success) {
        notify.error(payload?.message || 'Verification could not be completed');
        return;
      }

      setResult(payload);
      if (payload.user) persistUser(payload.user);
      await loadWallet();
      notify.success(payload.message || 'NIN verified successfully');
      setShowPinModal(false);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Verification failed'));
    } finally {
      setVerifying(false);
    }
  }

  async function handleDownloadPdf() {
    if (!result?.data || !slipType) return;
    setDownloading(true);
    try {
      const data = result.data;
      if (slipType === 'regular_slip') {
        await generateRegularNINSlipPDF({
          nin: data.nin || '',
          firstName: data.firstName || '',
          middleName: data.middleName || '',
          lastName: data.lastName || '',
          gender: data.gender || '',
          trackingId: data.trackingId,
          residenceAddress: data.residenceAddress,
          image: data.image,
        });
      } else if (slipType === 'standard_slip') {
        await generateStandardNINSlipPDF({
          nin: data.nin || '',
          firstName: data.firstName || '',
          middleName: data.middleName || '',
          lastName: data.lastName || '',
          gender: data.gender || '',
          dateOfBirth: data.dateOfBirth || '',
          image: data.image,
        });
      } else if (slipType === 'premium_slip') {
        await generatePremiumNINSlipPDF({
          nin: data.nin || '',
          firstName: data.firstName || '',
          middleName: data.middleName || '',
          lastName: data.lastName || '',
          gender: data.gender || '',
          dateOfBirth: data.dateOfBirth || '',
          image: data.image,
        });
      } else {
        await generateAdvancedNINSlipPDF({
          nin: data.nin || '',
          firstName: data.firstName || '',
          middleName: data.middleName || '',
          lastName: data.lastName || '',
          gender: data.gender || '',
          dateOfBirth: data.dateOfBirth || '',
          birthLga: data.birthLga,
          birthState: data.birthState,
          trackingId: data.trackingId,
          residenceAddress: data.residenceAddress,
          residenceTown: data.town,
          image: data.image,
        });
      }
    } catch (error) {
      notify.error('Failed to generate PDF. Ensure slip templates are installed in /public.');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  }

  if (!slipType) return null;

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Loading..." />
      </div>
    );
  }

  if (result?.success) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to={ROUTES.dashboardVerifyNin}
            className="inline-flex rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-brand-navy">{NIN_SLIP_LABELS[slipType]}</h1>
            <p className="text-sm text-brand-muted">Download your slip or verify another record.</p>
          </div>
        </div>
        <VerificationResultPanel result={result} onDownload={handleDownloadPdf} downloading={downloading} />
        <Button type="button" variant="outline" onClick={() => setResult(null)}>
          Verify another
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Link
          to={ROUTES.dashboardVerifyNin}
          className="mt-1 inline-flex rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-brand-navy">{NIN_SLIP_LABELS[slipType]}</h1>
          <p className="text-sm text-brand-muted">
            {NIN_METHOD_LABELS[verificationMethod]} · Pay from wallet and download slip instantly.
          </p>
        </div>
      </div>

      <Card className="mx-auto max-w-xl p-6">
        {verificationMethod === 'by_demography' ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">First name</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                  value={demography.firstName}
                  onChange={(e) => setDemography((d) => ({ ...d, firstName: e.target.value }))}
                />
                {demographyErrors.firstName && (
                  <p className="mt-1 text-xs text-red-600">{demographyErrors.firstName}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Last name</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                  value={demography.lastName}
                  onChange={(e) => setDemography((d) => ({ ...d, lastName: e.target.value }))}
                />
                {demographyErrors.lastName && (
                  <p className="mt-1 text-xs text-red-600">{demographyErrors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Gender</label>
              <select
                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                value={demography.gender}
                onChange={(e) => setDemography((d) => ({ ...d, gender: e.target.value }))}
              >
                <option value="">Select gender</option>
                <option value="m">Male</option>
                <option value="f">Female</option>
              </select>
              {demographyErrors.gender && (
                <p className="mt-1 text-xs text-red-600">{demographyErrors.gender}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Date of birth</label>
              <input
                type="date"
                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                value={
                  demography.dateOfBirth.includes('-') && demography.dateOfBirth.length === 10
                    ? `${demography.dateOfBirth.slice(6)}-${demography.dateOfBirth.slice(3, 5)}-${demography.dateOfBirth.slice(0, 2)}`
                    : ''
                }
                onChange={(e) =>
                  setDemography((d) => ({
                    ...d,
                    dateOfBirth: e.target.value ? formatDateForNinslip(e.target.value) : '',
                  }))
                }
              />
              {demographyErrors.dateOfBirth && (
                <p className="mt-1 text-xs text-red-600">{demographyErrors.dateOfBirth}</p>
              )}
            </div>
          </div>
        ) : (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">{inputCopy.label}</label>
            <div className="relative">
              <CreditCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm"
                placeholder={inputCopy.placeholder}
                value={ninValue}
                onChange={(e) => setNinValue(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="mt-6 rounded-xl bg-gray-50 px-4 py-3 text-sm">
          <p className="font-medium text-brand-navy">Amount to pay</p>
          <p className="text-lg font-bold text-brand-green">{price != null ? formatNaira(price) : '—'}</p>
          <p className="mt-1 text-gray-500">Wallet balance: ₦{(user?.walletBalance ?? 0).toLocaleString()}</p>
        </div>

        <Button
          type="button"
          fullWidth
          className="mt-6"
          disabled={!ready || price == null}
          onClick={() => setShowPinModal(true)}
        >
          Verify & Pay
        </Button>
      </Card>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={handleVerify}
        loading={verifying}
        title="Confirm NIN verification"
        description={`You will be charged ${price != null ? formatNaira(price) : ''} from your wallet.`}
      />
    </div>
  );
}
